import { BodySemi, CaptionRegular, SmallBodyRegular } from 'components/Text';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import {
    MdCheckBox,
    MdCheckBoxOutlineBlank,
    MdFilterList,
    MdIndeterminateCheckBox,
} from 'react-icons/md';
import {
    OverflowEllipsis,
    PlaceholderShimmerBackground,
} from 'utils/fragments';
import styled, { ThemeContext, css } from 'styled-components';
import { useContext, useEffect, useState } from 'react';
import ClusterIcon from 'components/ClusterIcon';
import ClusterTooltip from 'components/ClusterTooltip';
import DefinitionTooltip from 'components/DefinitionTooltip';
import HealthStatTooltip from '../../HealthStatTooltip';
import { HiSelector } from 'react-icons/hi';
import { PlaceholderText } from 'components/Placeholders';
import { PricePointMetricIcon } from '../../HealthStats';
import Stack from 'components/Stack';
import { Switch } from '@headlessui/react';
import Tooltip from 'components/Tooltip';
import { getGraphAttributes } from '../../GraphButton';
import { getScoreColor } from 'utils/helpers';
import { withMixpanelClick } from 'utils/app';

export const DataText = styled.span`
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.style_data};
`;

export const Table = styled.div`
    transition: all 200ms linear;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.gray_6};
    color: ${({ theme }) => theme.gray_2};
    border: solid 1px ${({ theme }) => theme.gray_5};
    max-height: ${({ $maxHeight }) =>
        $maxHeight || $maxHeight === 0 ? $maxHeight : '100vh'};
    transition: height 0.1s cubic-bezier(0.22, 0.61, 0.36, 1);
    overflow: auto;
    position: relative;
    z-index: 0;
    font-size: 1em;
`;

export const TableHead = styled.div`
    min-width: ${({ $minWidth }) => $minWidth}rem;
    width: 100%;
    user-select: none;
    position: sticky;
    top: 0;
    z-index: 1;
`;

export const TableBody = styled.div`
    display: block;
    min-width: ${({ $minWidth }) => $minWidth}rem;
    width: 100%;
    ${({ isFetching }) => (isFetching ? PlaceholderShimmerBackground : null)}
    ${({ isFetching, theme }) =>
        isFetching
            ? `background-color: ${theme.gray_6}; & > * {opacity: .5;}`
            : null}
`;

export const NoDataRow = styled.div`
    min-width: ${({ $minWidth }) => $minWidth}rem;
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${({ height }) => `${height}px`};
`;

export const BannerRow = styled.div`
    min-width: ${({ $minWidth }) => $minWidth}rem;
    display: flex;
    align-items: center;
    height: ${({ height }) => `${height}px`};
    background-color: ${({ theme }) => theme.purple_5};
`;

export const ColStyles = css`
    min-width: ${({ $minWidth }) => $minWidth}rem;
    & > :last-child {
        margin-right: 0.5em;
    }
    & > :first-child {
        margin-left: 0.5em;
    }
    padding-top: ${({ theme }) => `${theme.space_7}px`};
    padding-bottom: ${({ theme }) => `${theme.space_7}px`};
    padding-left: 0;
    padding-right: 0;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: ${({ $width }) => $width};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    border-right: solid 1px ${({ theme }) => theme.gray_5};

    ${({ active, theme }) =>
        active ? `&& { background-color: ${theme.purple_5}; }` : ''}

    &:last-of-type {
        border-right-width: 0px;
    }

    &:first-of-type {
        border-right-width: 1px;
    }
`;

const ClusterCol = css`
    text-align: center;
    justify-content: center;
`;

const GroupCol = css`
    text-align: left;
    justify-content: flex-start;
    min-width: 9rem;
    flex-basis: 183px;
`;

const GroupBodyCol = css`
    ${GroupCol}
    text-transform: lowercase;
`;

const RemoveCol = css`
    justify-content: center;
    font-size: 1.35em;
`;

const CheckboxCol = css`
    ${RemoveCol};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const TableHeadCol = styled.div`
    ${ColStyles};
    height: 39px;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ color, theme }) => color || theme.gray_2};
    text-transform: capitalize;
    ${({ colKey }) =>
        ['engagement_prc', 'convergence_prc'].includes(colKey)
            ? `
        overflow-wrap: anywhere;
        hyphens: auto;
    `
            : null}

    &&:hover, &&:focus-visible {
        background-color: ${({ theme, onClick }) =>
            onClick ? theme.purple_5 : theme.white};
        cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
    }

    ${({ colKey }) => {
        switch (colKey) {
            case 'toggleCheck':
                return CheckboxCol;
            case 'remove':
                return RemoveCol;
            default:
                return '';
        }
    }}
`;

export const TableCol = styled.div.attrs(getGraphAttributes)`
    ${ColStyles};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${({ color }) => color || 'inherit'};
    line-height: 24px;
    cursor: inherit;
    font-weight: inherit;
    ${({ colKey }) => {
        switch (colKey) {
            case 'name':
                return NameBodyCol;
            case 'creator':
                return CreatorBodyCol;
            case 'cluster':
                return ClusterCol;
            case 'parentName':
                return GroupBodyCol;
            case 'toggleCheck':
                return CheckboxCol;
            case 'remove':
                return RemoveCol;
            default:
                return '';
        }
    }}
`;

const TableRow = css`
    font-family: inherit;
    font-size: 1em;
    line-height: 1em;
    padding: 0;
    margin: 0;
    overflow: visible;
    text-transform: none;
    min-width: ${({ $minWidth }) => $minWidth}rem;
    width: 100%;
    display: flex;
    align-items: stretch;

    &:hover > *,
    &:focus-visible > * {
        ${({ onClick, theme }) =>
            onClick ? `background-color: ${theme.purple_5};` : ''}
        cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
    }

    height: 46px;
    border-width: 0px;
    font-weight: ${({ theme, selected }) =>
        selected ? theme.fontSemiBold : theme.fontMedium};

    & > *:last-of-type {
        border-right-width: 0px;
    }

    & > *:first-of-type {
        & > :first-child {
            margin-left: ${({ parentNode, theme }) =>
                parentNode ? '28' : theme.space_6}px;
        }
        border-right-width: 1px;
    }

    & > * {
        background-color: ${({ selected, theme }) =>
            selected ? theme.purple_5 : theme.gray_7};
        border-right: solid 1px ${({ theme }) => theme.gray_5};
        border-bottom: solid 1px ${({ theme }) => theme.gray_5};
    }
`;

export const TableRowBody = styled.div`
    ${TableRow}height: ${({ height }) => `${height}px`};
`;
export const VirtualRow = styled(TableRowBody).attrs(
    ({ $start, ...props }) => ({
        style: {
            transform: `translateY(${$start}px)`,
        },
        'data-testid': 'data-table-row',
        ...props,
    }),
)`
    position: absolute;
    top: 0;
    left: 0;
`;
export const TableRowHead = styled.div`
    ${TableRow}
    height: 40px;
`;
export const TableRowParent = styled.div`
    ${TableRow}
    height: ${({ height }) => `${height}px`};
    position: sticky;
    top: 39px;
    z-index: 1;
`;

export const TableHeadColFilterUntracked = styled(TableHeadCol)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 0.875em;
    color: ${({ active, theme }) => (active ? theme.purple_1 : theme.gray_2)};
    ${({ colKey }) => {
        switch (colKey) {
            case 'name':
            case 'creator':
                return NameCol;
            case 'cluster':
                return ClusterCol;
            case 'parentName':
                return GroupCol;
            default:
                return '';
        }
    }}
`;

export const TableHeadColFilter = withMixpanelClick(
    TableHeadColFilterUntracked,
    'TableHeadColFilter',
);

const ActiveOpacity = css`
    opacity: ${({ active }) => (active ? 1 : 0)};
    margin-right: ${({ theme }) => theme.space_2}px;
    font-size: 1.25em;
    flex-shrink: 0;
`;
const ArrowAscending = styled(({ active, ...props }) => (
    <FiArrowUp {...props} />
))`
    ${ActiveOpacity}
`;
const ArrowDescending = styled(({ active, ...props }) => (
    <FiArrowDown {...props} />
))`
    ${ActiveOpacity}
`;
export const SortIcon = styled(HiSelector)`
    margin-left: ${({ theme }) => theme.space_2}px;
    font-size: 1.25em;
    color: ${({ theme }) => theme.purple_1};
    flex-shrink: 0;
`;
export const FilterIndicator = styled(({ active, ...props }) => (
    <MdFilterList {...props} />
))`
    ${ActiveOpacity}
`;
export function SortIndicator({ active, ascending }) {
    return ascending ? (
        <ArrowAscending active={active} />
    ) : (
        <ArrowDescending active={active} />
    );
}

export const NameCol = css`
    flex-grow: 1;
    flex-shrink: 1;
    text-align: left;
    justify-content: flex-start;
    position: sticky;
    left: 0;
`;

export const NameBodyCol = css`
    ${NameCol};
    font-weight: inherit;
    line-height: 24px;
    text-transform: capitalize;
    color: ${({ theme }) => theme.black};
`;

const CreatorBodyCol = css`
    ${NameBodyCol};
    text-transform: none;
`;

export function TableClusterIcon({ cluster, fetching }) {
    return (
        <span
            css={`
                min-width: 18px;
                min-height: 18px;
                height: 1.5rem;
                display: flex;
                align-items: center;
                justify-content: center;
            `}
        >
            {cluster ? (
                <ClusterTooltip cluster={cluster}>
                    <ClusterIcon cluster={cluster} css="font-size: 1.125em;" />
                </ClusterTooltip>
            ) : fetching ? (
                <PlaceholderText width="1.125em" />
            ) : (
                '\u2013'
            )}
        </span>
    );
}

const ParentEntity = styled(CaptionRegular)`
    max-width: 100%;
    ${OverflowEllipsis};
`;

export function TableClusterNameCell({
    relatedName,
    name,
    cluster,
    fetching,
    nameToolTipContent,
    nameCss,
}) {
    return (
        <Stack
            $direction="row"
            $space="0.25rem"
            $wrap={false}
            css={
                'min-width: 0; width: 100%; display: flex; justify-content: space-between;'
            }
        >
            {nameToolTipContent ? (
                <Tooltip content={nameToolTipContent}>
                    <div>
                        <Stack
                            $space="-.25rem"
                            $direction="column"
                            css={`
                                min-width: 0;
                                ${nameCss}
                            `}
                        >
                            <ParentEntity as="div">{relatedName}</ParentEntity>
                            <BodySemi
                                as="div"
                                css="text-transform: capitalize;"
                            >
                                {name}
                            </BodySemi>
                        </Stack>
                    </div>
                </Tooltip>
            ) : (
                <Stack
                    $space="-.25rem"
                    $direction="column"
                    css={'min-width: 0;'}
                >
                    <ParentEntity as="div">{relatedName}</ParentEntity>
                    <BodySemi as="div" css="text-transform: capitalize;">
                        {name}
                    </BodySemi>
                </Stack>
            )}
            <Stack $space="-.25rem" $direction="column">
                <ParentEntity as="div">&nbsp;</ParentEntity>
                <TableClusterIcon cluster={cluster} fetching={fetching} />
            </Stack>
        </Stack>
    );
}

export const TableHealthStatLabel = styled.span`
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ $score, $metricLabel }) =>
        getScoreColor($score, false, $metricLabel)};
    &::first-letter {
        text-transform: uppercase;
    }
`;

export const TableHealthStatLabelInactive = styled(TableHealthStatLabel)`
    ${TableRowBody}:not(:hover):not(:focus-visible) & {
        color: ${({ theme }) => theme.gray_1};
    }
`;

export function ColumnTooltip({
    children,
    week,
    year,
    tooltip,
    mixpanelContentKey,
}) {
    if (!(tooltip && mixpanelContentKey)) return children;
    return (
        <DefinitionTooltip
            {...tooltip}
            week={week}
            year={year}
            mixpanelMeta={{ contentKey: mixpanelContentKey }}
        >
            {children}
        </DefinitionTooltip>
    );
}

const PricePointCellWrapper = styled.span`
    flex-grow: 1;
    text-transform: capitalize;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${({ theme }) => theme.gray_2};
`;

const PricePointCellMetricIcon = styled(PricePointMetricIcon)`
    color: ${({ theme }) => theme.gray_4};
`;

export function PricePointCell({ score, scoreLabel }) {
    return (
        <PricePointCellWrapper>
            {scoreLabel && (score === 0 || score > 0) ? (
                <>
                    {scoreLabel}
                    <HealthStatTooltip
                        metricLabel="price point"
                        label={scoreLabel}
                        score={score}
                    >
                        <PricePointCellMetricIcon score={score} />
                    </HealthStatTooltip>
                </>
            ) : (
                '-'
            )}
        </PricePointCellWrapper>
    );
}

function CheckboxSwitch({ isIntermediate, color, ...props }) {
    const themeContext = useContext(ThemeContext);
    let derivedColor = color || themeContext.gray_3;
    if (props.disabled) derivedColor = themeContext.gray_5;

    return (
        <Switch {...props}>
            {({ checked }) => {
                if (!checked)
                    return <MdCheckBoxOutlineBlank color={derivedColor} />;
                if (isIntermediate)
                    return <MdIndeterminateCheckBox color={derivedColor} />;
                return <MdCheckBox color={derivedColor} />;
            }}
        </Switch>
    );
}

export function ColCheckboxSwitch({
    as = TableCol,
    colKey = 'toggleCheck',
    onClick = (e) => e.stopPropagation(),
    ...props
}) {
    return (
        <CheckboxSwitch as={as} colKey={colKey} onClick={onClick} {...props} />
    );
}

export function ColCheckboxSwitchHead({
    data,
    onToggleCheck,
    $minWidth,
    $width,
}) {
    const themeContext = useContext(ThemeContext);
    const disabled = !(
        data && data.some(({ timeSeries }) => timeSeries && timeSeries.length)
    );
    const checked = new Boolean(data && data.some(({ checked }) => checked));
    const isAllChecked =
        data &&
        data.every(
            ({ checked, timeSeries }) =>
                checked || !(timeSeries && timeSeries.length),
        );
    const isIntermediate = checked && !isAllChecked;
    let color = themeContext.gray_3;

    if (isAllChecked) color = themeContext.purple_1;
    if (isIntermediate) color = themeContext.gray_3;
    if (disabled) color = themeContext.gray_5;

    return (
        <ColCheckboxSwitch
            disabled={disabled}
            checked={checked}
            onChange={(checked) => onToggleCheck({ checked })}
            isIntermediate={isIntermediate}
            color={color}
            $minWidth={$minWidth}
            $width={$width}
            as={TableHeadCol}
        />
    );
}

export const TikTokTableCol = styled(TableCol)`
    font-size: 0.925em;
    flex-grow: 1;
    flex-shrink: 1;
`;

export const TikTokTableHeadColFilter = withMixpanelClick(
    styled(TableHeadColFilterUntracked).attrs(
        ({ mixpanelContext = 'TableHeadColFilter' }) => ({
            mixpanelContext,
        }),
    )`
        font-size: 0.85em;
        & > :last-child {
            margin-right: 0.5em;
        }
        flex-grow: 1;
        flex-shrink: 1;
    `,
    'TableHeadColFilter',
);

export const RelatedHashtagsTableHeadColFilter = withMixpanelClick(
    styled(TableHeadColFilterUntracked).attrs(
        ({ mixpanelContext = 'TableHeadColFilter' }) => ({
            mixpanelContext,
        }),
    )`
        font-size: 0.875em;
        font-weight: 600;
        & > :last-child {
            margin-right: 0.5em;
        }
        flex-grow: 1;
        flex-shrink: 1;
    `,
    'TableHeadColFilter',
);

const MessagePositioner = styled.span`
    position: relative;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    z-index: 10;
`;
const MessageWrapper = styled.span`
    background: ${({ theme }) => theme.white};
    padding: 0.5rem 1.25rem;
    box-shadow: ${({ theme }) => theme.box_shadow_1};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    position: absolute;
    top: 5rem;
`;
export const DelayedLoadingMessage = ({ delay = 250, message }) => {
    const [showMessage, setShowMessage] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowMessage(true);
        }, delay);
        return () => clearTimeout(timer);
    }, [delay]);
    if (!showMessage) return null;
    return (
        <MessagePositioner>
            <MessageWrapper>
                <SmallBodyRegular>{message}</SmallBodyRegular>
            </MessageWrapper>
        </MessagePositioner>
    );
};
